import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import type { InferResponseType } from 'hono/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApplicationCard } from './application-card';

import type { api } from '~/lib/api';

type ApplicationsResponse = InferResponseType<
  (typeof api.api.org)[':orgId']['department'][':deptId']['jobs'][':jobId']['applications']['$get']
>;
type ApplicationItem = ApplicationsResponse['applications'][number];
type StageItem = ApplicationsResponse['stages'][number];

type ApplicationKanbanProps = {
  applications: ApplicationItem[];
  stages: StageItem[];
  onApplicationClick: (application: ApplicationItem) => void;
  onStageChange?: (applicationId: string, stageId: string) => void;
};

function DroppableColumn({
  stage,
  children
}: {
  stage: StageItem;
  children: React.ReactNode;
}): React.ReactNode {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });

  return (
    <div ref={setNodeRef} className="flex w-full flex-shrink-0 flex-col sm:w-72 sm:min-h-0">
      <div className="mb-3 flex items-center gap-2">
        {stage.color && (
          <div className="size-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
        )}
        <h3 className="text-sm font-semibold">{stage.name}</h3>
      </div>
      <div
        className={`flex min-h-[80px] flex-col gap-2 rounded-lg border border-dashed p-2 transition-colors sm:flex-1 sm:min-h-0 sm:overflow-y-auto ${
          isOver ? 'border-primary bg-primary/5' : 'border-transparent'
        }`}
      >
        {children}
        {!children ||
          (Array.isArray(children) && (children as React.ReactNode[]).length === 0 && (
            <p className="py-4 text-center text-xs text-muted-foreground">
              {t('applications.emptyStage')}
            </p>
          ))}
      </div>
    </div>
  );
}

function DraggableCard({
  application,
  onClick
}: {
  application: ApplicationItem;
  onClick: () => void;
}): React.ReactNode {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: application.id
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`cursor-grab touch-none ${isDragging ? 'opacity-30' : ''}`}
    >
      <ApplicationCard application={application} onClick={onClick} />
    </div>
  );
}

export function ApplicationKanban({
  applications,
  stages,
  onApplicationClick,
  onStageChange
}: ApplicationKanbanProps): React.ReactNode {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [stageOverrides, setStageOverrides] = useState<Map<string, string>>(new Map());

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const grouped = useMemo(() => {
    const map = new Map<string, ApplicationItem[]>();
    for (const stage of stages) {
      map.set(stage.id, []);
    }
    for (const app of applications) {
      const effectiveStageId = stageOverrides.get(app.id) ?? app.stageId;
      const list = map.get(effectiveStageId);
      if (list) list.push(app);
    }
    return map;
  }, [applications, stages, stageOverrides]);

  // Clear overrides once the parent data catches up
  useEffect(() => {
    if (stageOverrides.size === 0) return;
    const next = new Map<string, string>();
    for (const [appId, stageId] of stageOverrides) {
      const app = applications.find((a) => a.id === appId);
      if (app && app.stageId !== stageId) {
        next.set(appId, stageId);
      }
    }
    if (next.size !== stageOverrides.size) {
      setStageOverrides(next);
    }
  }, [applications, stageOverrides]);

  const activeApplication = useMemo(
    () => (activeId ? (applications.find((a) => a.id === activeId) ?? null) : null),
    [activeId, applications]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || !onStageChange) return;

      const applicationId = active.id as string;
      const newStageId = over.id as string;
      const app = applications.find((a) => a.id === applicationId);
      if (!app || app.stageId === newStageId) return;

      setStageOverrides((prev) => new Map(prev).set(applicationId, newStageId));
      onStageChange(applicationId, newStageId);
    },
    [applications, onStageChange]
  );

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex flex-col gap-4 pb-4 sm:flex-row sm:flex-1 sm:min-h-0 sm:overflow-x-auto">
        {stages.map((stage) => {
          const stageApps = grouped.get(stage.id) ?? [];
          return (
            <DroppableColumn key={stage.id} stage={stage}>
              {stageApps.length === 0 ? (
                <p className="py-4 text-center text-xs text-muted-foreground">
                  {t('applications.emptyStage')}
                </p>
              ) : (
                stageApps.map((app) => (
                  <DraggableCard
                    key={app.id}
                    application={app}
                    onClick={() => {
                      onApplicationClick(app);
                    }}
                  />
                ))
              )}
            </DroppableColumn>
          );
        })}
      </div>
      <DragOverlay>
        {activeApplication && (
          <div className="w-72 rotate-2 opacity-90 shadow-lg">
            <ApplicationCard application={activeApplication} onClick={() => {}} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
